/**
 * Sitewide Sale — Recharge one-time price.
 *
 * Paints the theme's Sitewide Sale markdown onto the ONE-TIME option of the Recharge
 * subscription widget, which otherwise renders the full Shopify variant price and disagrees
 * with the price block above it.
 *
 * Prices are NOT computed here. 'snippets/sitewide-sale-onetime-price.liquid' resolves them
 * in Liquid (tag gate, sale window, on/off switches, rounding, money formatting) and hands
 * them over as JSON, so this file only has to decide which variant is showing.
 *
 * Everything is drawn by CSS through ::part(), driven by a custom property and a mode
 * attribute set on the widget's host element. Nothing reaches into the shadow DOM, so
 * Recharge re-rendering cannot wipe the markdown out and we never fight its renderer.
 */
(function () {
  'use strict';

  var DATA_ID = 'sw-onetime-sale-data';
  var MODE_ATTR = 'data-sw-price-mode';
  var SHADOW_POLL_MS = 50;
  var SHADOW_POLL_TRIES = 100;

  var byVariant = null;

  function data() {
    if (byVariant) return byVariant;
    byVariant = {};
    var el = document.getElementById(DATA_ID);
    if (!el) return byVariant;
    try {
      var rows = (JSON.parse(el.textContent) || {}).variants || [];
      for (var i = 0; i < rows.length; i++) byVariant[String(rows[i].id)] = rows[i];
    } catch (e) {
      /* Malformed payload: leave the widget exactly as Recharge rendered it. */
    }
    return byVariant;
  }

  /**
   * The widget exposes `product-id` and `default-variant-id` as attributes, but the
   * CURRENTLY selected variant is Lit internal state — there is no `variant-id` attribute
   * to read, so the live value only exists as a property on the element.
   */
  function variantId(w) {
    if (w.variantId) return String(w.variantId);
    var fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl) return String(fromUrl);
    return String(w.getAttribute('default-variant-id') || '');
  }

  function clear(w) {
    w.removeAttribute(MODE_ATTR);
    w.style.removeProperty('--sw-onetime-sale');
    w.style.removeProperty('--sw-onetime-compare');
  }

  function apply(w) {
    var entry = data()[variantId(w)];

    // Writing to style/attributes mutates the host, which our own observers would see.
    // Skipping an unchanged write keeps that from looping.
    var key = entry ? entry.mode + '|' + entry.sale + '|' + entry.compare : 'none';
    if (key === w.__swOnetimeKey) return;
    w.__swOnetimeKey = key;

    if (!entry) {
      clear(w);
      return;
    }

    // JSON.stringify gives a quoted, escaped string, which is what CSS `content` wants.
    if (entry.mode === 'sitewide') {
      w.style.setProperty('--sw-onetime-sale', JSON.stringify(entry.sale));
      w.style.removeProperty('--sw-onetime-compare');
    } else {
      w.style.setProperty('--sw-onetime-compare', JSON.stringify(entry.compare));
      w.style.removeProperty('--sw-onetime-sale');
    }
    w.setAttribute(MODE_ATTR, entry.mode);
  }

  function attach(w) {
    if (w.__swOnetimeBound) return;
    w.__swOnetimeBound = true;

    var queued = false;
    function run() {
      if (queued) return;
      queued = true;
      setTimeout(function () {
        queued = false;
        apply(w);
      }, 0);
    }

    // Only the attributes Recharge owns — watching our own writes would be circular.
    new MutationObserver(run).observe(w, {
      attributes: true,
      attributeFilter: ['product-id', 'default-variant-id', 'default-selling-plan-id']
    });

    // A re-render of the shadow tree is the one reliable signal that the selected variant
    // changed, since the variant itself never lands in an attribute.
    var watching = false;
    function watchShadow() {
      if (watching || !w.shadowRoot) return false;
      watching = true;
      new MutationObserver(run).observe(w.shadowRoot, {
        childList: true,
        subtree: true,
        characterData: true
      });
      run();
      return true;
    }

    if (!watchShadow()) {
      // The element can be in the DOM before it upgrades and builds its shadow root.
      var tries = 0;
      var poll = setInterval(function () {
        if (watchShadow() || ++tries >= SHADOW_POLL_TRIES) clearInterval(poll);
      }, SHADOW_POLL_MS);
    }

    document.addEventListener('variant:change', run);
    document.addEventListener('variantChange', run);
    run();
  }

  function scan() {
    var widgets = document.querySelectorAll('recharge-subscription-widget');
    for (var i = 0; i < widgets.length; i++) attach(widgets[i]);
  }

  scan();
  new MutationObserver(scan).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
